'use client'

import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
// @ts-ignore
import ReactEcharts from 'echarts-for-react'
import 'katex/dist/katex.min.css'
import RemarkMath from 'remark-math'
import RemarkBreaks from 'remark-breaks'
import RehypeKatex from 'rehype-katex'
import RemarkGfm from 'remark-gfm'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atelierHeathLight } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import type { RefObject } from 'react'
import clsx from 'clsx'
import { CopyIcon as CopyBtn, SvgIcon as SVGBtn, FlowIcon as Flowchart } from '../icons/svgr'
import './markdown.scss'
import RequirementTable from '../RequirementTable'

// Available language https://github.com/react-syntax-highlighter/react-syntax-highlighter/blob/master/AVAILABLE_LANGUAGES_HLJS.MD
const capitalizationLanguageNameMap: Record<string, string> = {
  sql: 'SQL',
  javascript: 'JavaScript',
  java: 'Java',
  typescript: 'TypeScript',
  vbscript: 'VBScript',
  css: 'CSS',
  html: 'HTML',
  xml: 'XML',
  php: 'PHP',
  python: 'Python',
  yaml: 'Yaml',
  mermaid: 'Mermaid',
  markdown: 'MarkDown',
  makefile: 'MakeFile',
  echarts: 'ECharts',
}

const getCorrectCapitalizationLanguageName = (language: string) => {
  if (!language)
    return 'Plain'

  if (language in capitalizationLanguageNameMap)
    return capitalizationLanguageNameMap[language]

  return language.charAt(0).toUpperCase() + language.substring(1)
}

const preprocessLaTeX = (content: string) => {
  if (typeof content !== 'string')
    return content
  // @ts-ignore
  return content.replace(/\\\[(.*?)\\\]/gs, (_, equation) => `$$${equation}$$`).replace(/\\\((.*?)\\\)/gs, (_, equation) => `$$${equation}$$`).replace(/(^|[^\\])\$(.+?)\$/gs, (_, prefix, equation) => `${prefix}$${equation}$`)
}

export function PreCode(props: { children: any }) {
  const ref = useRef<HTMLPreElement>(null)

  return (
    <pre ref={ref}>
      <span
        className="copy-code-button"
        onClick={() => {
          if (ref.current) {
            const code = ref.current.innerText
            // copyToClipboard(code);
          }
        }}
      ></span>
      {props.children}
    </pre>
  )
}

const useLazyLoad = (ref: RefObject<Element>): boolean => {
  const [isIntersecting, setIntersecting] = useState<boolean>(false)

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIntersecting(true)
        observer.disconnect()
      }
    })

    if (ref.current)
      observer.observe(ref.current)

    return () => {
      observer.disconnect()
    }
  }, [ref])

  return isIntersecting
}

// 定义代码组件的类型
interface CodeBlockProps {
  inline?: boolean
  className?: string
  children?: React.ReactNode
  [key: string]: any
}

const CodeBlock: React.FC<CodeBlockProps> = ({ inline, className, children, ...props }) => {
  const [isSVG, setIsSVG] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match?.[1];
  const languageShowName = getCorrectCapitalizationLanguageName(language || '');
  
  let chartData = JSON.parse(String('{"title":{"text":"Something went wrong."}}').replace(/\n$/, ''));
  if (language === 'echarts') {
    try {
      chartData = JSON.parse(String(children).replace(/\n$/, ''));
    } catch (error) {
      console.error('Error parsing chart data:', error);
    }
  }

  return useMemo(() => {
    if (inline) {
      return <code className={className} {...props}>{children}</code>;
    }

    return (
      <div>
        <div className='flex justify-between h-8 items-center p-1 pl-3 border-b'
          style={{
            borderColor: 'rgba(0, 0, 0, 0.05)',
          }}>
          <div className='text-[13px] text-gray-500 font-normal'>{languageShowName}</div>
          <div style={{ display: 'flex' }}>
            {language === 'mermaid' && (
              <SVGBtn
                isSVG={isSVG}
                setIsSVG={setIsSVG}
              />
            )}
            <CopyBtn
              className='mr-1'
              value={String(children).replace(/\n$/, '')}
              isPlain
            />
          </div>
        </div>
        {language === 'mermaid' && isSVG ? (
          <Flowchart PrimitiveCode={String(children).replace(/\n$/, '')} />
        ) : language === 'echarts' ? (
          <div style={{ minHeight: '250px', minWidth: '250px' }}>
            <ReactEcharts option={chartData} />
          </div>
        ) : (
          <SyntaxHighlighter
            style={atelierHeathLight}
            customStyle={{
              paddingLeft: 12,
              backgroundColor: '#fff',
            }}
            language={language || 'text'}
            showLineNumbers
            PreTag="div"
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        )}
      </div>
    );
  }, [children, className, inline, isSVG, language, languageShowName, props, chartData]);
};

CodeBlock.displayName = 'CodeBlock';

// 修改 Image 组件
const Image: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
  const { src, alt, ...rest } = props;
  return (
    <div className="markdown-image-container">
      <img 
        src={src}
        alt={alt || ''}
        className="markdown-image"
        loading="lazy"
        {...rest}
      />
    </div>
  );
};

interface RequirementItem {
  description: string;
  priority: '🔥 高优先级' | '⚠️ 潜在风险' | '💡 洞察';
}

// 为表格单元格添加特定CSS类的辅助函数
const addPriorityClass = (content: string): string => {
  if (content.includes('🔥')) return 'high-priority';
  if (content.includes('⚠️')) return 'medium-priority';
  if (content.includes('💡')) return 'insight-priority';
  return '';
};

export const Markdown = memo(function (props: { content: string; className?: string }) {
  const latexContent = preprocessLaTeX(props.content);
  
  // 自定义渲染组件
  const customComponents = {
    // 代码块渲染
    code: CodeBlock,
    // 图片渲染
    img: Image,
    // 段落处理
    p: (paragraph: any) => {
      const { node } = paragraph;
      if (node?.children?.[0]?.tagName === 'img') {
        const image = node.children[0];
        return (
          <div className="markdown-image-container">
            <img
              src={image.properties.src}
              alt={image.properties.alt || ''}
              className="markdown-image"
              loading="lazy"
            />
            {paragraph.children?.length > 1 && (
              <p>{paragraph.children.slice(1)}</p>
            )}
          </div>
        );
      }
      return <p>{paragraph.children}</p>;
    },
    // 为表格添加special-table类
    table: ({ children, ...props }: any) => (
      <table className="markdown-table" {...props}>
        {children}
      </table>
    ),
    // 为表头添加special-th类
    th: ({ children, ...props }: any) => (
      <th className="markdown-th" {...props}>
        {children}
      </th>
    ),
    // 为表格单元格添加special-td类和优先级类
    td: ({ children, ...props }: any) => {
      // 将子元素转换为字符串以检查优先级图标
      const childContent = React.Children.toArray(children)
        .map(child => 
          typeof child === 'string' 
            ? child 
            : React.isValidElement(child) 
              ? (child.props.children || '') 
              : ''
        )
        .join('');
      
      const priorityClass = addPriorityClass(childContent);
      
      return (
        <td 
          className={clsx("markdown-td", priorityClass && priorityClass)} 
          {...props}
        >
          {children}
        </td>
      );
    }
  };

  // 处理需求表格
  const processRequirementTable = (content: string): { tableSection: string; hasTable: boolean; cleanContent: string } => {
    // 两种可能的表格格式
    const tablePattern1 = /\|\s*需求编号\s*\|\s*需求描述\s*\|\s*优先级\s*\|/;
    const tablePattern2 = /\|\s*需求描述\s*\|\s*优先级\s*\|/;
    
    let startIndex = -1;
    let tableHeader = '';
    
    // 查找第一种格式
    if (tablePattern1.test(content)) {
      startIndex = content.indexOf('| 需求编号');
      tableHeader = '| 需求编号';
    } 
    // 查找第二种格式
    else if (tablePattern2.test(content)) {
      startIndex = content.indexOf('| 需求描述');
      tableHeader = '| 需求描述';
    }
    
    // 如果找到表格
    if (startIndex !== -1) {
      const endIndex = content.indexOf('\n\n', startIndex) > 0 
          ? content.indexOf('\n\n', startIndex) 
          : content.length;
      const tableSection = content.substring(startIndex, endIndex);
      
      // 创建不包含表格的内容
      const cleanContent = content.substring(0, startIndex) + 
                           (endIndex < content.length ? content.substring(endIndex) : '');
      
      return { tableSection, hasTable: true, cleanContent };
    }
    
    return { tableSection: '', hasTable: false, cleanContent: content };
  };
  
  // 处理内容
  const { tableSection, hasTable, cleanContent } = processRequirementTable(props.content);
  const cleanLatexContent = hasTable ? preprocessLaTeX(cleanContent) : latexContent;

  return (
    <div className={clsx('markdown-container', 'markdown-body', props.className)}>
      <ReactMarkdown 
        components={customComponents}
        remarkPlugins={[RemarkMath, RemarkGfm, RemarkBreaks]}
        rehypePlugins={[RehypeKatex]}
      >
        {cleanLatexContent}
      </ReactMarkdown>
      {hasTable && <RequirementTable markdownText={tableSection} />}
    </div>
  );
});

Markdown.displayName = 'Markdown'; 